import { action, observable, computed } from 'mobx'
import {
  type DocumentType,
  type WellKnownDocumentTypeShortId,
} from '@/features/documents/DocumentType'
import type { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { randomUUID } from '@/utils/uuid-util'

const otherTypeShortId: WellKnownDocumentTypeShortId = 'OTHER'

/**
 * View model for choosing the type of a file to upload.
 */
export class DocumentCreationItemViewModel {
  /**
   * Identifies the item while files are added and removed.
   */
  readonly id = randomUUID()

  @observable accessor selectedType: DocumentType | null = null
  @observable accessor searchQuery = ''

  readonly dropdown = new ModalViewModel()

  constructor(
    readonly file: File,
    private readonly _getTypes: () => DocumentType[],
  ) {}

  /**
   * The types the file can be given.
   */
  @computed
  get types() {
    return this._getTypes()
  }

  /**
   * The selected type's name, or a prompt to select one.
   */
  @computed
  get typeName() {
    return this.selectedType?.name ?? 'Select type'
  }

  @computed
  get filteredTypes(): DocumentType[] {
    const sorted = [...this.types].sort((a, b) => a.name.localeCompare(b.name))
    if (!this.searchQuery.trim()) return sorted
    const query = this.searchQuery.toLowerCase().trim()
    return sorted.filter((type) => type.name.toLowerCase().includes(query))
  }

  /**
   * The "Other" type, which is listed apart from the rest.
   */
  @computed
  get otherType() {
    return this.types.find((t) => t.id === otherTypeShortId) ?? null
  }

  /**
   * The matching types, except "Other".
   */
  @computed
  get typesWithoutOther() {
    return this.filteredTypes.filter((t) => t.id !== otherTypeShortId)
  }

  /**
   * The matching types to list, except "Other".
   */
  @computed
  get typeItems() {
    return this.typesWithoutOther.map((type) => this._toTypeItem(type))
  }

  /**
   * The "Other" type to list apart from the rest, if available.
   */
  @computed
  get otherTypeItem() {
    return this.otherType ? this._toTypeItem(this.otherType) : null
  }

  @computed
  get hasNoMatchingTypes() {
    return !!this.searchQuery.trim() && this.filteredTypes.length === 0
  }

  @action.bound
  setSearchQuery(query: string) {
    this.searchQuery = query
  }

  @action.bound
  clearSearchQuery() {
    this.searchQuery = ''
  }

  @action.bound
  toggleDropdown() {
    if (this.dropdown.showing) {
      this.dropdown.close()
    } else {
      this.dropdown.show()
    }
  }

  @action.bound
  closeDropdown() {
    this.dropdown.close()
    this.searchQuery = ''
  }

  @action.bound
  setDocumentType(type: DocumentType) {
    this.selectedType = type
  }

  /**
   * Selects one of the listed types and closes the dropdown.
   */
  @action.bound
  selectType(id: Id) {
    const type = [...this.typesWithoutOther, this.otherType].find(
      (t) => t?.id === id,
    )
    if (type) {
      this.setDocumentType(type)
      this.closeDropdown()
    }
  }

  private _toTypeItem(type: DocumentType) {
    return {
      id: type.id,
      name: type.name,
      selected: this.selectedType === type,
    }
  }
}
